import { css, keyframes } from "@emotion/css";
import { Badge, Callout, RadioButtonNew, RadioGroupNew } from "@octopusdeploy/design-system-components";
import { space, text } from "@octopusdeploy/design-system-tokens";
import type { GitRef, PolicyBffResource } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import semver from "semver/preload";
import { PolicyBanner } from "~/areas/platformHub/policies/PolicyBanner";
import { GitRefChip } from "~/areas/projects/components/Releases/GitRefChip/GitRefChip";
export interface PublishPolicyDialogVersion {
    Version: string;
}
interface PublishPolicyDialogProps {
    policy: PolicyBffResource;
    gitRef: GitRef;
    versionToPublish: string;
    setVersionToPublish: (version: string) => void;
    latestPublishedVersion: PublishPolicyDialogVersion | undefined;
    releaseType: PolicyReleaseType;
    setReleaseType: (releaseType: PolicyReleaseType) => void;
    defaultVersion: string;
}
export function PublishPolicyDialog({ policy, gitRef, versionToPublish, setVersionToPublish, latestPublishedVersion, releaseType, setReleaseType, defaultVersion }: PublishPolicyDialogProps) {
    if (policy.Warning) {
        return (<div className={styles.dialogContainer}>
                <Callout type="warning" title="This policy has unresolved warnings.">
                    Unresolved warnings must be fixed before the policy can be published.
                </Callout>
            </div>);
    }
    const onReleaseTypeChanged = (newReleaseType: PolicyReleaseType) => {
        setReleaseType(newReleaseType);
        updatePublishVersion(newReleaseType);
    };
    const updatePublishVersion = (releaseType: PolicyReleaseType) => {
        if (!latestPublishedVersion) {
            setVersionToPublish(defaultVersion);
            return;
        }
        setVersionToPublish(incrementVersionByReleaseType(latestPublishedVersion, releaseType) ?? defaultVersion);
    };
    const versionInfo = (<div>
            {latestPublishedVersion ? (<span className={styles.versionContainer}>
                    <span>{latestPublishedVersion.Version}</span>
                    <span>→</span>
                    <span className={styles.versionBadge} key={versionToPublish}>
                        <Badge size={"medium"} label={versionToPublish} variant={"info"}/>
                    </span>
                </span>) : (<span className={styles.versionContainer}>{versionToPublish}</span>)}
        </div>);
    const gitRefChip = <GitRefChip vcsRef={{ GitRef: gitRef }} maxGitRefLength={25}/>;
    return (<div className={styles.dialogContainer}>
            <PolicyBanner heading={policy.Name} subHeading={versionInfo} accessories={gitRefChip}/>
            <div>
                <h3 className={styles.releaseTypeHeading}>Release type</h3>
                {latestPublishedVersion ? null : <div className={styles.firstVersionNote}>The first version of a policy will always be a major release.</div>}
                <RadioGroupNew value={releaseType} onChange={(rt) => onReleaseTypeChanged(rt)} label="">
                    <RadioButtonNew name="Major" value="Major" label="Major changes (breaking)" disabled={!latestPublishedVersion}/>
                    <RadioButtonNew name="Minor" value="Minor" label="Minor changes (non-breaking)" disabled={!latestPublishedVersion}/>
                    <RadioButtonNew name="Patch" value="Patch" label="Patch (bug fixes)" disabled={!latestPublishedVersion}/>
                </RadioGroupNew>
            </div>
        </div>);
}
export type PolicyReleaseType = "Major" | "Minor" | "Patch";
export function incrementVersionByReleaseType(latestVersion: PublishPolicyDialogVersion, releaseType: PolicyReleaseType) {
    switch (releaseType) {
        case "Major":
            return semver.inc(latestVersion.Version, "major");
        case "Minor":
            return semver.inc(latestVersion.Version, "minor");
        case "Patch":
            return semver.inc(latestVersion.Version, "patch");
    }
}
const animations = {
    fade: keyframes({
        from: {
            opacity: 0.6,
        },
        to: {
            opacity: 1,
        },
    }),
};
const styles = {
    dialogContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[32],
    }),
    name: css({
        font: text.body.bold.medium,
    }),
    versionContainer: css({
        display: "flex",
        gap: space[6],
        alignItems: "center",
        font: text.body.regular.small,
    }),
    versionBadge: css({
        animation: `${animations.fade} 0.2s ease-in-out`,
        "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
        },
    }),
    releaseTypeHeading: css({
        font: text.heading.small,
        margin: `0 0 ${space[12]} 0`,
    }),
    firstVersionNote: css({
        font: text.body.regular.medium,
        margin: `0 0 ${space[12]} 0`,
    }),
};
